/**
 * Times and clock geometry for the telling-time worksheet.
 *
 * Angles are degrees clockwise from 12 o'clock. The minute hand moves 6° per minute; the
 * hour hand moves 30° per hour and half a degree per minute, so at 3:30 it points halfway
 * between the 3 and the 4, as a real clock's does.
 */

import { collect } from "@/lib/tool-kit/worksheets/arithmetic";
import { pick, type Rng, randInt } from "@/lib/tool-kit/worksheets/random";

export type ClockLevel = "hour" | "half" | "quarter" | "five" | "minute";
export type ClockTask = "read" | "draw";
export type ClockTaskMode = ClockTask | "mixed";

export interface ClockProblem {
  /** 1 to 12. */
  hour: number;
  /** 0 to 59. */
  minute: number;
  /** `read`: write the time shown. `draw`: draw the hands for the time given. */
  task: ClockTask;
}

export interface ClockOptions {
  count: number;
  level: ClockLevel;
  task: ClockTaskMode;
}

const LEVEL_MINUTES: Record<Exclude<ClockLevel, "minute">, number[]> = {
  hour: [0],
  half: [0, 30],
  quarter: [0, 15, 30, 45],
  five: [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55],
};

/** The minutes a level can show; `minute` allows all 60. */
export function minutesFor(level: ClockLevel): number[] {
  return level === "minute"
    ? Array.from({ length: 60 }, (_, i) => i)
    : LEVEL_MINUTES[level];
}

export function generateClocks(
  options: ClockOptions,
  rng: Rng
): ClockProblem[] {
  const minutes = minutesFor(options.level);
  const { items } = collect(
    options.count,
    rng,
    (r) => ({
      item: {
        hour: randInt(r, 1, 12),
        minute: pick(r, minutes),
        task:
          options.task === "mixed"
            ? pick(r, ["read", "draw"] as const)
            : options.task,
      },
      ok: true,
    }),
    (p) => `${p.hour}:${p.minute}:${p.task}`
  );
  return items;
}

/** `3:05` — hour without a leading zero, minutes with one. */
export function formatTime(hour: number, minute: number): string {
  return `${hour}:${String(minute).padStart(2, "0")}`;
}

export interface HandAngles {
  hour: number;
  minute: number;
}

export function handAngles(hour: number, minute: number): HandAngles {
  return {
    hour: (hour % 12) * 30 + minute * 0.5,
    minute: minute * 6,
  };
}

export interface Point {
  x: number;
  y: number;
}

/**
 * Coordinates are rounded to three decimals. `Math.sin` and `Math.cos` may differ in the
 * last bit between Node and a browser, and an SVG attribute that differs by one digit
 * between the server's HTML and the client's render is a hydration mismatch.
 */
function roundCoordinate(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/** The point `length` from the centre at `angle` degrees clockwise from 12. */
export function polar(
  cx: number,
  cy: number,
  angle: number,
  length: number
): Point {
  const radians = (angle * Math.PI) / 180;
  return {
    x: roundCoordinate(cx + length * Math.sin(radians)),
    y: roundCoordinate(cy - length * Math.cos(radians)),
  };
}

/** The 12 clock numerals around a circle of the given radius. */
export function numeralPositions(
  cx: number,
  cy: number,
  radius: number
): { value: number; x: number; y: number }[] {
  return Array.from({ length: 12 }, (_, i) => {
    const value = i + 1;
    return { value, ...polar(cx, cy, value * 30, radius) };
  });
}
